import { SEO } from "@/components/SEO";
import {
  ArrowRight,
  CheckCircle2,
  CreditCard,
  Database,
  FileCheck2,
  Globe,
  KeyRound,
  Server,
  ShieldCheck,
  Smartphone,
} from "lucide-react";

const payUrl = "/pay/BSA-2026-010";
const onboardingUrl = "/onboard";

const findings = [
  { label: "Frontend pages", value: "53" },
  { label: "Components", value: "80" },
  { label: "PostgreSQL tables", value: "62" },
  { label: "API routes", value: "90" },
  { label: "Audio files", value: "412" },
  { label: "Codebase size", value: "97k+" },
];

const scope = [
  {
    icon: Globe,
    title: "Production website",
    detail: "Move Nourish Me Health from a Replit-style setup into a real production web deployment.",
  },
  {
    icon: Server,
    title: "Backend and API",
    detail: "Configure the server/API environment so the app can run outside the current development container.",
  },
  {
    icon: Database,
    title: "Database migration",
    detail: "Prepare PostgreSQL configuration, environment access, and production data migration path.",
  },
  {
    icon: Smartphone,
    title: "iPhone app path",
    detail: "Package the application for iPhone, TestFlight, and App Store preparation.",
  },
  {
    icon: KeyRound,
    title: "Secrets and providers",
    detail: "Set up environment variables, API keys, and third-party service configuration safely.",
  },
  {
    icon: ShieldCheck,
    title: "QA and handoff",
    detail: "Test the website, backend flows, and iPhone app path before the final handoff.",
  },
];

const phases = [
  {
    phase: "01",
    title: "Kickoff and access",
    window: "Week 1",
    detail: "Confirm the selected option, collect company details, receive Replit/source access, database access, API keys, domain/hosting access, and Apple Developer status.",
  },
  {
    phase: "02",
    title: "Production foundation",
    window: "Weeks 2-6",
    detail: "Move the project out of the development setup, configure backend hosting, database connections, deployment flow, and production environment variables.",
  },
  {
    phase: "03",
    title: "App packaging and QA",
    window: "Weeks 6-14",
    detail: "Prepare the iPhone app path, run migration QA, fix launch-blocking issues, and validate website/backend behavior.",
  },
  {
    phase: "04",
    title: "Handoff and launch prep",
    window: "Final stretch",
    detail: "Finalize the deployment, document the handoff, confirm remaining balance, and prepare for ongoing maintenance if selected.",
  },
];

const startRequirements = [
  "Replit/source code access",
  "Database access or export",
  "Environment variables and API keys",
  "Domain and hosting preference",
  "Apple Developer account status",
  "Signed 5% equity terms if the strategic partner option is selected",
];

const exclusions = [
  "Hosting",
  "Apple Developer fees",
  "Paid APIs",
  "SMS/email usage",
  "AI/model usage",
  "Database, storage, or bandwidth overages",
  "App Store/platform fees",
  "Third-party outages, provider pricing changes, API policy changes, account issues, or approval decisions",
];

export default function HazeTVGamePlanPage() {
  return (
    <main className="min-h-screen bg-[#f6f8fb] text-slate-950">
      <SEO
        title="Nourish Me Health Migration Plan | Better Systems AI"
        description="Client-facing migration plan, timeline, pricing options, and kickoff steps for Nourish Me Health."
      />

      <section className="border-b border-slate-200 bg-white">
        <div className="mx-auto max-w-6xl px-6 py-12 sm:py-16">
          <div className="grid gap-10 lg:grid-cols-[1fr_360px] lg:items-start">
            <div>
              <p className="text-sm font-bold uppercase tracking-[0.2em] text-blue-700">Migration brief</p>
              <h1 className="mt-4 max-w-4xl text-4xl font-semibold leading-tight tracking-tight text-slate-950 sm:text-6xl">
                Nourish Me Health production launch plan
              </h1>
              <p className="mt-5 max-w-3xl text-lg leading-8 text-slate-600">
                I reviewed the current application footprint. This is not a small website move. It is a full-stack health and wellness platform that needs a controlled production migration for web, backend, database, and iPhone readiness.
              </p>
              <div className="mt-8 flex flex-col gap-3 sm:flex-row">
                <a
                  href={onboardingUrl}
                  className="inline-flex items-center justify-center rounded-md bg-blue-700 px-5 py-3 text-sm font-semibold text-white transition hover:bg-blue-800"
                >
                  Submit kickoff details
                  <ArrowRight className="ml-2 h-4 w-4" />
                </a>
                <a
                  href={payUrl}
                  className="inline-flex items-center justify-center rounded-md border border-slate-300 bg-white px-5 py-3 text-sm font-semibold text-slate-950 transition hover:bg-slate-100"
                >
                  Open deposit page
                  <CreditCard className="ml-2 h-4 w-4" />
                </a>
              </div>
            </div>

            <aside className="rounded-xl border border-slate-200 bg-slate-50 p-6 shadow-sm">
              <div className="text-sm font-semibold text-slate-950">Prepared for</div>
              <div className="mt-1 text-sm text-slate-600">Andrew Haze TV</div>
              <div className="mt-5 text-sm font-semibold text-slate-950">Reference app</div>
              <a className="mt-1 block text-sm font-medium text-blue-700 hover:text-blue-800" href="https://nourishmehealth.org" target="_blank" rel="noreferrer">
                nourishmehealth.org
              </a>
              <div className="mt-5 border-t border-slate-200 pt-5">
                <div className="text-sm font-semibold text-slate-950">Recommended path</div>
                <p className="mt-2 text-sm leading-6 text-slate-600">
                  Strategic partner option: $12,000 plus 5% equity, with $7,200 due before kickoff.
                </p>
              </div>
            </aside>
          </div>
        </div>
      </section>

      <section className="bg-[#111827] text-white">
        <div className="mx-auto max-w-6xl px-6 py-8">
          <div className="grid gap-4 sm:grid-cols-3 lg:grid-cols-6">
            {findings.map((item) => (
              <div key={item.label} className="border-l border-white/20 pl-4">
                <div className="text-3xl font-semibold tracking-tight">{item.value}</div>
                <div className="mt-1 text-xs font-medium uppercase tracking-[0.16em] text-slate-300">{item.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-white">
        <div className="mx-auto max-w-6xl px-6 py-12">
          <div className="grid gap-10 lg:grid-cols-[360px_1fr]">
            <div>
              <p className="text-sm font-bold uppercase tracking-[0.18em] text-blue-700">Analysis</p>
              <h2 className="mt-3 text-3xl font-semibold tracking-tight">What the app needs</h2>
              <p className="mt-4 text-sm leading-7 text-slate-600">
                The app needs a structured migration, not a quick file transfer. The work has to protect the backend, database, app packaging, credentials, and launch flow.
              </p>
            </div>
            <div className="grid gap-4 sm:grid-cols-2">
              {scope.map((item) => {
                const Icon = item.icon;
                return (
                  <div key={item.title} className="rounded-lg border border-slate-200 bg-white p-5 shadow-sm">
                    <Icon className="h-5 w-5 text-blue-700" />
                    <h3 className="mt-4 text-base font-semibold">{item.title}</h3>
                    <p className="mt-2 text-sm leading-6 text-slate-600">{item.detail}</p>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      <section className="border-y border-slate-200 bg-[#f6f8fb]">
        <div className="mx-auto max-w-6xl px-6 py-12">
          <div className="grid gap-8 lg:grid-cols-[1fr_420px]">
            <div>
              <p className="text-sm font-bold uppercase tracking-[0.18em] text-blue-700">Roadmap</p>
              <h2 className="mt-3 text-3xl font-semibold tracking-tight">3-6 month execution plan</h2>
              <p className="mt-4 max-w-2xl text-sm leading-7 text-slate-600">
                The timeline depends on source code quality, database condition, App Store timing, and how quickly access is provided.
              </p>
              <div className="mt-8 divide-y divide-slate-200 rounded-xl border border-slate-200 bg-white shadow-sm">
                {phases.map((item) => (
                  <div key={item.phase} className="grid gap-4 p-5 sm:grid-cols-[70px_1fr_120px]">
                    <div className="text-sm font-semibold text-blue-700">{item.phase}</div>
                    <div>
                      <h3 className="text-base font-semibold">{item.title}</h3>
                      <p className="mt-2 text-sm leading-6 text-slate-600">{item.detail}</p>
                    </div>
                    <div className="text-sm font-semibold text-slate-500 sm:text-right">{item.window}</div>
                  </div>
                ))}
              </div>
            </div>

            <div className="rounded-xl border border-slate-200 bg-white p-6 shadow-sm">
              <h3 className="text-xl font-semibold tracking-tight">Start requirements</h3>
              <p className="mt-2 text-sm leading-6 text-slate-600">
                These need to be handled before meaningful production work can start.
              </p>
              <div className="mt-5 space-y-3">
                {startRequirements.map((item) => (
                  <div key={item} className="flex gap-3 text-sm leading-6 text-slate-700">
                    <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-blue-700" />
                    <span>{item}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="bg-white">
        <div className="mx-auto max-w-6xl px-6 py-12">
          <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
            <div>
              <p className="text-sm font-bold uppercase tracking-[0.18em] text-blue-700">Commercial options</p>
              <h2 className="mt-3 text-3xl font-semibold tracking-tight">Choose the structure</h2>
            </div>
            <p className="max-w-xl text-sm leading-7 text-slate-600">
              The strategic partner option is discounted because Better Systems AI would participate in project upside through documented equity.
            </p>
          </div>

          <div className="mt-8 overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
            <div className="grid bg-slate-100 px-5 py-3 text-xs font-bold uppercase tracking-[0.16em] text-slate-500 sm:grid-cols-[1fr_1fr_1fr]">
              <div>Option</div>
              <div>Cash terms</div>
              <div>Ownership terms</div>
            </div>
            <div className="grid gap-0 border-t border-slate-200 sm:grid-cols-[1fr_1fr_1fr]">
              <div className="border-b border-slate-200 p-5 sm:border-b-0 sm:border-r">
                <div className="text-sm font-semibold text-slate-500">Option A</div>
                <div className="mt-2 text-xl font-semibold">Standard Project</div>
                <div className="mt-5 text-4xl font-semibold">$16,250</div>
              </div>
              <div className="border-b border-slate-200 p-5 sm:border-b-0 sm:border-r">
                <div className="text-sm leading-7 text-slate-700">
                  <div>$8,125 to start</div>
                  <div>$8,125 before final handoff</div>
                </div>
              </div>
              <div className="p-5 text-sm leading-7 text-slate-700">No equity required.</div>
            </div>
            <div className="grid gap-0 border-t-2 border-blue-700 bg-blue-50/60 sm:grid-cols-[1fr_1fr_1fr]">
              <div className="border-b border-blue-100 p-5 sm:border-b-0 sm:border-r">
                <div className="text-sm font-semibold text-blue-700">Option B</div>
                <div className="mt-2 text-xl font-semibold">Strategic Partner</div>
                <div className="mt-5 text-4xl font-semibold">$12,000 + 5%</div>
              </div>
              <div className="border-b border-blue-100 p-5 sm:border-b-0 sm:border-r">
                <div className="text-sm leading-7 text-slate-700">
                  <div>$7,200 to start</div>
                  <div>$4,800 before final handoff</div>
                </div>
              </div>
              <div className="p-5 text-sm leading-7 text-slate-700">
                5% equity must be documented before kickoff and tied to the actual company/project receiving the software.
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="border-y border-slate-200 bg-[#f6f8fb]">
        <div className="mx-auto max-w-6xl px-6 py-12">
          <div className="grid gap-8 lg:grid-cols-[420px_1fr]">
            <div>
              <p className="text-sm font-bold uppercase tracking-[0.18em] text-blue-700">Kickoff</p>
              <h2 className="mt-3 text-3xl font-semibold tracking-tight">Next step</h2>
              <p className="mt-4 text-sm leading-7 text-slate-600">
                Review the plan, submit the project details, and complete the kickoff deposit. Work begins after deposit payment and signed equity terms if the strategic partner option is selected.
              </p>
              <div className="mt-6 flex flex-col gap-3">
                <a
                  href={onboardingUrl}
                  className="inline-flex items-center justify-center rounded-md bg-blue-700 px-5 py-3 text-sm font-semibold text-white transition hover:bg-blue-800"
                >
                  Submit kickoff details
                  <ArrowRight className="ml-2 h-4 w-4" />
                </a>
                <a
                  href={payUrl}
                  className="inline-flex items-center justify-center rounded-md border border-slate-300 bg-white px-5 py-3 text-sm font-semibold text-slate-950 transition hover:bg-slate-100"
                >
                  Open deposit page
                  <CreditCard className="ml-2 h-4 w-4" />
                </a>
              </div>
            </div>

            <div className="rounded-xl border border-slate-200 bg-white p-6 shadow-sm">
              <h3 className="text-xl font-semibold tracking-tight">Third-party costs are not included</h3>
              <p className="mt-2 text-sm leading-6 text-slate-600">
                These outside costs remain the client responsibility and are billed directly or reimbursed separately.
              </p>
              <div className="mt-5 grid gap-2 sm:grid-cols-2">
                {exclusions.map((item) => (
                  <div key={item} className="rounded-md border border-slate-200 bg-slate-50 px-3 py-2 text-sm leading-5 text-slate-700">
                    {item}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="bg-white">
        <div className="mx-auto flex max-w-6xl flex-col gap-4 px-6 py-8 text-sm leading-6 text-slate-600 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex items-center gap-2">
            <FileCheck2 className="h-4 w-4 text-blue-700" />
            <span>Quote valid for 14 days.</span>
          </div>
          <div className="max-w-2xl">
            Final technical risk can only be confirmed after source code, database access, API keys, and current Replit configuration are reviewed.
          </div>
        </div>
      </section>
    </main>
  );
}
